'use client';

import { Zap, Headset, CircuitBoard } from 'lucide-react';
import { Suspense, useEffect } from 'react';
import { Canvas, useThree } from '@react-three/fiber';
import { OrbitControls, Center, Preload } from '@react-three/drei';
import { useProtectedGLTF } from '@/lib/3d/useProtectedGLTF';
import { logo2Url } from '@/lib/constants/models';
import * as THREE from 'three';
import Link from 'next/link';

function LogoModel() {
  const { scene } = useProtectedGLTF(logo2Url);
  const { camera, gl } = useThree();

  useEffect(() => {
    const raycaster = new THREE.Raycaster();
    const pointer = new THREE.Vector2();

    let mouseDownX = 0;
    let mouseDownY = 0;
    let isDragging = false;
    const DRAG_THRESHOLD = 5; // pixels of movement before considered a drag

    const onPointerDown = (e: PointerEvent) => {
      mouseDownX = e.clientX;
      mouseDownY = e.clientY;
      isDragging = false;
    };

    const onPointerMove = (e: PointerEvent) => {
      const rect = gl.domElement.getBoundingClientRect();

      // Check if dragging
      const dx = e.clientX - mouseDownX;
      const dy = e.clientY - mouseDownY;
      if (Math.sqrt(dx * dx + dy * dy) > DRAG_THRESHOLD) {
        isDragging = true;
      }

      // Cursor feedback
      pointer.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      pointer.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

      raycaster.setFromCamera(pointer, camera);
      scene.updateMatrixWorld(true);

      const intersects = raycaster.intersectObjects(scene.children, true);
      gl.domElement.style.cursor = intersects.length > 0 && !isDragging ? 'pointer' : 'default';
    };

    const onClick = (e: MouseEvent) => {
      // Ignore if the user was dragging
      if (isDragging) return;

      const rect = gl.domElement.getBoundingClientRect();
      pointer.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      pointer.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;

      raycaster.setFromCamera(pointer, camera);
      scene.updateMatrixWorld(true);

      const intersects = raycaster.intersectObjects(scene.children, true);

      if (intersects.length > 0) {
        window.open('https://trioe.dev/', '_blank');
      }
    };

    gl.domElement.addEventListener('pointerdown', onPointerDown);
    gl.domElement.addEventListener('pointermove', onPointerMove);
    gl.domElement.addEventListener('click', onClick);

    return () => {
      gl.domElement.removeEventListener('pointerdown', onPointerDown);
      gl.domElement.removeEventListener('pointermove', onPointerMove);
      gl.domElement.removeEventListener('click', onClick);
      gl.domElement.style.cursor = 'default';
    };
  }, [scene, camera, gl]);

  return (
    <Center>
      <primitive object={scene} scale={0.15} />
    </Center>
  );
}

interface HeroSectionProps {
  isDark: boolean;
}

export function HeroSection({ isDark }: HeroSectionProps) {
  return (
    <div className="w-full py-12 sm:py-16 lg:py-20">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="flex flex-col lg:flex-row lg:items-center gap-8 lg:gap-12">

          {/* Left Column - Text Content */}
          <div className="flex-1">
            <h1 className={`text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 lg:mb-6 text-center lg:text-left bg-gradient-to-r ${isDark ? 'from-white via-blue-200 to-blue-400' : 'from-black via-blue-900 to-blue-600'} bg-clip-text text-transparent`}>
              Welcome to Electrop3Dia
            </h1>
            <p className={`text-base sm:text-lg lg:text-xl ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-6 lg:mb-8 text-center lg:text-left`}>
              Explore the fascinating world of 3D design, electrical engineering, and cutting-edge technology.
              Learn about circuit boards, semiconductors, and the future of electronics.
            </p>

            {/* Buttons */}
            <div className="flex flex-col items-center lg:items-start gap-4">
              <div className="flex flex-col sm:flex-row items-center gap-4">
                <Link
                  href="/learn"
                  className={`flex items-center justify-center gap-2 w-[260px] px-6 py-3 rounded-lg transition-all shadow-lg ${
                    isDark
                      ? 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-blue-500/50 hover:shadow-blue-500/70'
                      : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white shadow-blue-400/50 hover:shadow-blue-400/70'
                  }`}
                >
                  Start Learning
                  <Zap className="w-5 h-5" />
                </Link>

                <Link
                  href="/simulate"
                  className={`flex items-center justify-center gap-2 px-6 py-3 rounded-lg transition-all shadow-lg ${
                    isDark
                      ? 'bg-gradient-to-r from-emerald-600 to-teal-700 hover:from-emerald-700 hover:to-teal-800 text-white shadow-emerald-500/50 hover:shadow-emerald-500/70'
                      : 'bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white shadow-emerald-400/50 hover:shadow-emerald-400/70'
                  }`}
                >
                  Start 3D Simulation Workbench
                  <CircuitBoard className="w-5 h-5" />
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/20 border border-white/40">
                    Beta
                  </span>
                </Link>
              </div>

              <div>
                <p className={`text-sm mb-2 text-center lg:text-left ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  <br />
                  <br />
                  Have a VR HEADSET? Click below
                </p>
                <Link
                  href="/learn/vr"
                  className={`flex items-center justify-center gap-2 w-[260px] px-6 py-3 rounded-lg transition-all shadow-lg ${
                    isDark
                      ? 'bg-gradient-to-r from-purple-600 to-indigo-700 hover:from-purple-700 hover:to-indigo-800 text-white shadow-purple-500/50 hover:shadow-purple-500/70'
                      : 'bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white shadow-purple-400/50 hover:shadow-purple-400/70'
                  }`}
                >
                  Start Learning in VR
                  <Headset className="w-5 h-5" />
                </Link>
              </div>
            </div>
          </div>

          {/* Right Column - 3D Model */}
          <div className="flex-1 mt-8 lg:mt-0">
            <div className={`relative bg-gradient-to-br ${isDark ? 'from-blue-950/50 to-blue-900/30' : 'from-blue-100/50 to-blue-50/30'} rounded-2xl border ${isDark ? 'border-blue-500/30' : 'border-blue-300/30'} p-4 sm:p-6 backdrop-blur-sm`}>

              {/* Background Text */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-0">
                <span className={`text-6xl sm:text-8xl md:text-9xl lg:text-[10rem] font-black select-none opacity-30 ${isDark ? 'text-blue-800' : 'text-blue-300'}`}>
                  TRIOE
                </span>
              </div>

              {/* 3D Canvas */}
              <div className="w-full h-[250px] sm:h-[300px] lg:h-[350px] relative z-10">
                <Canvas
                  camera={{ position: [0, 0, 1.5], fov: 14 }}
                  gl={{ antialias: true }}
                  dpr={[1, 2]}
                  style={{ touchAction: 'auto' }}
                >
                  <ambientLight intensity={0.6} />
                  <directionalLight position={[5, 5, 5]} intensity={1.2} />
                  <directionalLight position={[-5, -5, -2]} intensity={0.4} />
                  <pointLight position={[0, 0, 2]} intensity={0.8} />
                  <OrbitControls
                    enablePan={true}
                    enableZoom={true}
                    enableRotate={true}
                    autoRotate={true}
                    autoRotateSpeed={-7.5}
                    minDistance={1}
                    maxDistance={3}
                    zoomSpeed={0.8}
                  />
                  <Suspense fallback={null}>
                    <LogoModel />
                    <Preload all />
                  </Suspense>
                </Canvas>
              </div>

              <div className="relative z-10 mt-3 text-center">
                <p className={`text-xs sm:text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  Interactive 3D Logo
                </p>
                <p className={`text-[10px] sm:text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'} mt-1`}>
                  Click and drag to rotate
                </p>
              </div>

              <p className="absolute bottom-2 right-2 text-[8px] sm:text-[10px] text-gray-500 z-10">
                MADE WITH LOVE FOR THE TRIOE COMMUNITY
              </p>
              <p className="absolute bottom-2 left-2 text-[8px] sm:text-[10px] text-gray-500 z-10">
                TRY CLICKING THE LOGO ;)
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}